import React, { useEffect, useState, useCallback } from "react";
import UserList from "./UserList";
import { axiosClient } from "../api/proxy/AxiosClient";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Grid2,
  Chip,
} from "@mui/material"; 
import Schedule from "./Schedule";

export default function CreateLessonByTeacher({
  open,
  onClose,
  onSave,
  setNewLesson,
  users,
  selectedStudentsRef,
  scheduleRef
}) {

  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [fee, setFee] = useState(100);
  const [ccy, setCcy] = useState("USD");
  const [link, setLink] = useState("");
  const [localSelectedUsers, setLocalSelectedUsers] = useState(
    selectedStudentsRef.current || []
  );
  const [localSchedule, setLocalSchedule] = useState(scheduleRef.current || { start: null, end: null });
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  const [events, setEvents] = useState([]);
  const [errors, setErrors] = useState({});

  const user = JSON.parse(localStorage.getItem("user"));

  const validate = () => {
    let errs = {};

    // teacherUid
    if (!user?.uid) {
      errs.teacherUid = "Teacher is required";
    }

    // students
    if (!localSelectedUsers.length) {
      errs.studentUids = "At least one student is required";
    }

    // schedule
    if (!localSchedule.start) {
      errs.startDttm = "Start date is required";
    }

    if (!localSchedule.end) {
      errs.endDttm = "End date is required";
    }

    if (localSchedule.start && localSchedule.end) {
      if (new Date(localSchedule.end) <= new Date(localSchedule.start)) {
        errs.endDttm = "End date must be after start date";
      }
    }

    // fee
    if (fee === null || fee === "" || isNaN(fee)) {
      errs.fee = "Fee is required";
    } else if (Number(fee) <= 0) {
      errs.fee = "Fee must be greater than 0";
    }

    // currency
    if (!ccy) {
      errs.ccy = "Currency is required";
    } else if (!/^[A-Z]{3}$/.test(ccy)) {
      errs.ccy = "Currency must be 3 uppercase letters";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
    

  const handleSave = async () => {
    if (!validate()) {
      return;
    }
    onSave({
      name,
      studentUids: localSelectedUsers.map(s => s.uid),
      teacherUid: user.uid,
      startDttm: localSchedule.start,
      endDttm: localSchedule.end,
      fee: parseFloat(fee),
      ccy,
      link,
      comment,
    }).then((newLessonResponse) => {
      setNewLesson(newLessonResponse.data);
      onClose();
      setName("");
      setFee(0);
      setCcy("USD");
      setLink("");
      setLocalSelectedUsers([]);
      setLocalSchedule({ start: null, end: null });
    }).catch (err => {
      console.error(err.message);
    });
  }

  const handleStudentsChange = (arr) => {
    setLocalSelectedUsers(arr);
    selectedStudentsRef.current = arr;
  };

  const handleRangeSelected = (range) => {
    setLocalSchedule(range);
    scheduleRef.current = range;
    setScheduleModalOpen(false);
  };

  const removeStudent = (uid) => {
    const updated = localSelectedUsers.filter(s => s.uid !== uid);
    handleStudentsChange(updated);
  };

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axiosClient.get(`/lessons?teacherUid=${user.uid}`);
      const lessonRequests = response.data;

      const formattedEvents = lessonRequests.map((request) => ({
        id: request.id,
        uid: request.uid,
        title: `[${request.name}]`,
        start: new Date(request.startDttm),
        end: new Date(request.endDttm),
        status: request.status,
        allDay: false,
        lesson: request
      }));

      setEvents(formattedEvents);
    } catch (error) {
      console.error("Error fetching events:", error);
      return [];
    }
  }, [user?.uid]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" keepMounted>
        <DialogTitle>Create Lesson</DialogTitle>
        <DialogContent dividers>
          <Grid2 container spacing={2}>
            <Grid2>
              <Box mb={2}>
                <TextField
                  label="Lesson Name"
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Box>

              <Box mb={2}>
                <TextField
                  label="Lesson Comment"
                  fullWidth
                  multiline
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </Box>

              <Box mb={2}>
                <TextField
                  label="From - Till"
                  fullWidth
                  value={
                      (localSchedule.start ? new Date(localSchedule.start).toLocaleString(undefined, {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false
                      }) : "--/--/---- --:--") +
                      " - " +
                      (localSchedule.end ? new Date(localSchedule.end).toLocaleTimeString(undefined, {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: false
                      }) : "--:--")
                  }
                  onClick={() => setScheduleModalOpen(true)}
                  readOnly
                />
              </Box>

              <Box mb={2}>
                <div>Students:</div>
                <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                  {localSelectedUsers.map((student) => (
                    <Chip
                      key={student.uid}
                      label={student.firstName + ' ' + student.lastName}
                      onDelete={() => removeStudent(student.uid)}
                    />
                  ))}
                </Box>
              </Box>

              <Box mb={2}>
                <TextField
                  label="Fee"
                  type="number"
                  fullWidth
                  value={fee}
                  onChange={(e) => {
                    setFee(e.target.value);
                    setErrors(prev => ({ ...prev, fee: null }));
                  }}
                  error={!!errors.fee}
                  helperText={errors.fee}
                />
              </Box>

              <Box mb={2}>
                <TextField
                  label="Currency"
                  fullWidth
                  value={ccy}
                  onChange={(e) => {
                    setCcy(e.target.value.toUpperCase());
                    setErrors(prev => ({ ...prev, ccy: null }));
                  }}
                  slotProps={{ input: { maxLength: 3 } }}
                  error={!!errors.ccy}
                  helperText={errors.ccy}
                />
              </Box>

              <Box mb={2}>
                <TextField
                  label="Lesson Link"
                  fullWidth
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                />
              </Box>
            </Grid2>

            <Grid2>
              <UserList
                users={users}
                multiselect={true}
                selectedUsers={localSelectedUsers}
                setSelectedUsers={setLocalSelectedUsers}
              />
            </Grid2>
          </Grid2>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Select Schedule</DialogTitle>
        <DialogContent dividers>
          <Schedule
            start={localSchedule.start}
            end={localSchedule.end}
            onRangeSelected={handleRangeSelected}
            selectedUsers={localSelectedUsers.length > 0 ? localSelectedUsers : []}
            events={events}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
